/**
 * harness.ts — train/eval split evaluation harness (v2: pickup/skip reframe).
 *
 * Ported from `_proto_kitchen_v2.py` `run_train_eval` + `run_flip_schedule` +
 * `gen_pretrain_data`. Mirrors reflex's harness interface (runHarness +
 * HarnessEvent stream) so harness_worker.ts stays verbatim.
 *
 * Protocol per arm:
 *   1. PRETRAIN (if teacher data supplied): seed the long buffer + fit head.
 *   2. TRAIN: N_TRAIN_WORLDS episodes with online learning (counterfactual
 *      both-arm observations).
 *   3. EVAL: N_EVAL_WORLDS held-out episodes with model frozen (evalMode).
 *
 * Reports subset-accuracy per (phase, arm, seed) and drift events on the
 * flip schedule (H3). Subset-accuracy is the headline metric: the linear
 * foil's wrong-context pickups compound -3 penalties, so its basket score
 * collapses to ~chance even though its per-item pickup accuracy on the
 * 4 stable items is 1.000.
 */
import {
  N_ACTIONS, N_FEATURES, SKIP, PICKUP, HEADLINE_THEME,
  N_TRAIN_WORLDS, N_EVAL_WORLDS, FLIP_AT_STEP,
  MORNING, EVENING, ITEM_VALUE,
  type ArmMode, type DriftStatus,
} from './constants';
import { KitchenBandit } from './bandit';
import { KitchenEnv } from './environment';
import { makeWorld, makeRng, type Grid } from './shapes';
import {
  subsetAccuracy, scoreBasket, pickupReward,
  firstTimeF1FromLog, duplicateRateFromLog,
} from './items';

export interface PhaseRow {
  phase: 'train' | 'eval';
  worldInPhase: number;
  subsetAcc: number;
  basketScore: number;
  ctx: number;
  f1: number;            // first-time F1 (positive class = should PICKUP)
  dupRate: number;       // duplicate-pickup rate (must be 0 post-constraint)
  nDup: number;          // duplicate encounter count
}

export interface TrainEvalArmResult {
  arm: ArmMode;
  perSeed: Array<{ train: PhaseRow[]; eval: PhaseRow[] }>;
}

export interface FlipArmResult {
  arm: ArmMode;
  perSeed: Array<{ accs: number[]; drift: Array<{ n: number; status: DriftStatus; err: number }> }>;
}

export interface HarnessConfig {
  seeds: number[];
  nTrain: number;
  nEval: number;
  nFlip: number;
  minSamplesLeaf: number;
  nTrees: number;
  pretrainX: Float64Array | null;
  pretrainY: Float64Array | null;
  arms: ArmMode[];
}

export const DEFAULT_HARNESS_CONFIG: HarnessConfig = {
  seeds: [0, 1, 2, 3, 4],
  nTrain: N_TRAIN_WORLDS,
  nEval: N_EVAL_WORLDS,
  nFlip: 12,
  minSamplesLeaf: 3,
  nTrees: 40,
  pretrainX: null,
  pretrainY: null,
  arms: ['ts', 'greedy', 'linear', 'random'],
};

/** Generate teacher pretrain data via the optimal context-aware oracle:
 *  at each item, emit BOTH (skip-features, 0) and (pickup-features, delta)
 *  counterfactual rows. This gives the head balanced 2-arm data and is the
 *  single most important thing for fast convergence.
 *
 *  Returns flat Float64Arrays of shape (n × N_FEATURES) and (n). */
export function genPretrainData(nEpisodes: number, seed: number): {
  X: Float64Array; y: Float64Array;
} {
  const Xs: Float64Array[] = [];
  const ys: number[] = [];
  const ctxRng = makeRng(seed ^ 0xa5a5a5);
  for (let i = 0; i < nEpisodes; i++) {
    const ws = seed * 100_000 + i;
    const ctx = Math.floor(ctxRng() * 2);
    const world = makeWorld(HEADLINE_THEME, makeRng(ws)).grid;
    const env = new KitchenEnv(world as Grid, ctx);
    env.reset();
    const buf = new Float64Array(N_ACTIONS * N_FEATURES);
    while (!env.done) {
      if (env.currentItem < 0) break;
      const item = env.currentItem;
      const curCtx = env.ctx;
      const basket = env.basket;
      // Counterfactual rewards (no env mutation). pickupReward applies the
      // duplicate penalty if the item type is already in the basket.
      const pickupDelta = pickupReward(basket, item, curCtx);
      env.candidateFeatures(buf);
      Xs.push(buf.slice(SKIP * N_FEATURES, (SKIP + 1) * N_FEATURES));
      ys.push(0);
      Xs.push(buf.slice(PICKUP * N_FEATURES, (PICKUP + 1) * N_FEATURES));
      ys.push(pickupDelta);
      // Teacher's actual decision: pickup iff positive in ctx.
      const a = ITEM_VALUE[item][curCtx] > 0 ? PICKUP : SKIP;
      env.step(a);
    }
  }
  const n = ys.length;
  const X = new Float64Array(n * N_FEATURES);
  const y = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    X.set(Xs[i], i * N_FEATURES);
    y[i] = ys[i];
  }
  return { X, y };
}

export type HarnessEvent =
  | { kind: 'progress'; arm: ArmMode; seed: number; phase: 'train' | 'eval' | 'flip'; worldIdx: number; nWorlds: number }
  | { kind: 'arm-done'; arm: ArmMode }
  | { kind: 'done'; trainEval: TrainEvalArmResult[]; flip: FlipArmResult[] }
  | { kind: 'error'; message: string };

export type HarnessSink = (e: HarnessEvent) => void;

function runEpisode(
  env: KitchenEnv, agent: KitchenBandit, record = false,
): {
  basket: number; ctx: number;
  log: Array<{ item: number; ctx: number; a: number; isDup: boolean }>;
} {
  env.reset();
  const buf = new Float64Array(N_ACTIONS * N_FEATURES);
  const log: Array<{ item: number; ctx: number; a: number; isDup: boolean }> = [];
  while (!env.done) {
    if (env.currentItem < 0) break;
    env.candidateFeatures(buf);
    const { item, ctx, basket } = env.adjacentContext();
    const legal = env.legalActions();
    const { action } = agent.select(buf, legal);
    // Counterfactual rewards for both arms (no env mutation). pickupReward
    // applies the duplicate penalty for already-collected item types.
    const pickupDelta = pickupReward(basket, item, ctx);
    const takenReward = action === PICKUP ? pickupDelta : 0;
    const isDup = (basket & (1 << item)) !== 0;
    const { done } = env.step(action);
    if (record) log.push({ item, ctx, a: action, isDup });
    // Observe BOTH arms counterfactually (balanced 2-arm training).
    agent.observe(
      buf.slice(SKIP * N_FEATURES, (SKIP + 1) * N_FEATURES),
      SKIP, 0, action, takenReward,
    );
    agent.observe(
      buf.slice(PICKUP * N_FEATURES, (PICKUP + 1) * N_FEATURES),
      PICKUP, pickupDelta, action, takenReward,
    );
    if (done) break;
  }
  return { basket: env.basket, ctx: env.ctx, log };
}

function runTrainEvalArm(arm: ArmMode, cfg: HarnessConfig, sink: HarnessSink): TrainEvalArmResult {
  const perSeed: TrainEvalArmResult['perSeed'][number][] = [];
  for (const seed of cfg.seeds) {
    const agent = new KitchenBandit({
      mode: arm, seed,
      minSamplesLeaf: cfg.minSamplesLeaf, nTrees: cfg.nTrees,
    });
    if (cfg.pretrainX && cfg.pretrainY) agent.pretrain(cfg.pretrainX, cfg.pretrainY);
    const train: PhaseRow[] = [];
    for (let w = 0; w < cfg.nTrain; w++) {
      const wrng = makeRng(seed * 100_000 + 7000 + w);
      const { grid, ctx } = makeWorld(HEADLINE_THEME, wrng);
      const env = new KitchenEnv(grid as Grid, ctx);
      const { basket, ctx: endCtx, log } = runEpisode(env, agent, true);
      const f1 = firstTimeF1FromLog(log).f1;
      const { rate: dupRate, n: nDup } = duplicateRateFromLog(log);
      train.push({
        phase: 'train', worldInPhase: w,
        subsetAcc: subsetAccuracy(basket, endCtx),
        basketScore: scoreBasket(basket, endCtx), ctx: endCtx,
        f1, dupRate, nDup,
      });
      sink({ kind: 'progress', arm, seed, phase: 'train', worldIdx: w, nWorlds: cfg.nTrain });
    }
    agent.evalMode = true;
    const ev: PhaseRow[] = [];
    for (let w = 0; w < cfg.nEval; w++) {
      const wrng = makeRng(seed * 100_000 + 9000 + w);
      const { grid, ctx } = makeWorld(HEADLINE_THEME, wrng);
      const env = new KitchenEnv(grid as Grid, ctx);
      const { basket, ctx: endCtx, log } = runEpisode(env, agent, true);
      const f1 = firstTimeF1FromLog(log).f1;
      const { rate: dupRate, n: nDup } = duplicateRateFromLog(log);
      ev.push({
        phase: 'eval', worldInPhase: w,
        subsetAcc: subsetAccuracy(basket, endCtx),
        basketScore: scoreBasket(basket, endCtx), ctx: endCtx,
        f1, dupRate, nDup,
      });
      sink({ kind: 'progress', arm, seed, phase: 'eval', worldIdx: w, nWorlds: cfg.nEval });
    }
    perSeed.push({ train, eval: ev });
  }
  return { arm, perSeed };
}

function runFlipArm(arm: ArmMode, cfg: HarnessConfig, sink: HarnessSink): FlipArmResult {
  const perSeed: FlipArmResult['perSeed'][number][] = [];
  for (const seed of cfg.seeds) {
    const agent = new KitchenBandit({
      mode: arm, seed,
      minSamplesLeaf: cfg.minSamplesLeaf, nTrees: cfg.nTrees,
    });
    const accs: number[] = [];
    for (let w = 0; w < cfg.nFlip; w++) {
      const wrng = makeRng(seed * 100_000 + w);
      const { grid } = makeWorld(HEADLINE_THEME, wrng);
      const env = new KitchenEnv(grid as Grid, MORNING, FLIP_AT_STEP, EVENING);
      const { basket } = runEpisode(env, agent);
      accs.push(subsetAccuracy(basket, EVENING));
      sink({ kind: 'progress', arm, seed, phase: 'flip', worldIdx: w, nWorlds: cfg.nFlip });
    }
    perSeed.push({ accs, drift: agent.driftEvents.slice() });
  }
  return { arm, perSeed };
}

export function runHarness(cfg: HarnessConfig, sink: HarnessSink): void {
  try {
    const trainEval: TrainEvalArmResult[] = [];
    const flip: FlipArmResult[] = [];
    for (const arm of cfg.arms) {
      trainEval.push(runTrainEvalArm(arm, cfg, sink));
      flip.push(runFlipArm(arm, cfg, sink));
      sink({ kind: 'arm-done', arm });
    }
    sink({ kind: 'done', trainEval, flip });
  } catch (err) {
    sink({ kind: 'error', message: (err as Error).message });
  }
}

/** Helper for the UI: aggregate per-arm mean eval accuracy across seeds. */
export function meanEvalAcc(r: TrainEvalArmResult): number {
  let sum = 0;
  let n = 0;
  for (const ps of r.perSeed) {
    for (const row of ps.eval) { sum += row.subsetAcc; n++; }
  }
  return n > 0 ? sum / n : 0;
}

/** Helper: per-arm mean first-time F1 across seeds (the honest decision
 *  metric; reported alongside subset-acc in the eval table). */
export function meanEvalF1(r: TrainEvalArmResult): number {
  let sum = 0;
  let n = 0;
  for (const ps of r.perSeed) {
    for (const row of ps.eval) { sum += row.f1; n++; }
  }
  return n > 0 ? sum / n : 0;
}

/** Helper: per-arm duplicate-pickup rate across eval worlds. Must be 0
 *  after the legal-mask fix — a regression check on the constraint. */
export function meanEvalDupRate(r: TrainEvalArmResult): number {
  let wrong = 0;
  let nDup = 0;
  for (const ps of r.perSeed) {
    for (const row of ps.eval) { wrong += row.dupRate * row.nDup; nDup += row.nDup; }
  }
  return nDup > 0 ? wrong / nDup : 0;
}

/** Helper: count drifts on the flip schedule for an arm. */
export function countDrifts(r: FlipArmResult): number {
  let n = 0;
  for (const ps of r.perSeed) {
    for (const e of ps.drift) if (e.status === 'drift') n++;
  }
  return n;
}
